// frontend/src/pages/CalendarPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { format, parse, startOfWeek, getDay } from "date-fns";
import "../components/QuotesPage.css";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// pdfjs worker (match your installed pdfjs-dist)
// Uses your local file in /public

const locales = {};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// --- API helpers ---
async function fetchJobs() {
  const res = await fetch("/api/jobs");
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  const json = await res.json();
  return (json.data || []).map((e) => ({
    id: e.id,
    title: e.title || `Quote ${e.quote_id}`,
    start: new Date(e.start),
    end: new Date(e.end),
    quoteId: e.quote_id,
  }));
}
async function createJob({ quote_id, start, duration_hours }) {
  const res = await fetch("/api/jobs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ quote_id, start, duration_hours }),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json();
}
async function fetchJobDetail(id) {
  const res = await fetch(`/api/jobs/${id}`);
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json();
}
async function createInvoiceFromJob(jobId, paid) {
  const res = await fetch(`/api/jobs/${jobId}/invoice`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paid: !!paid }),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json(); // { id, invoice_number, existing? }
}
async function emailInvoice(id, subject, message) {
  const res = await fetch(`/api/invoices/${id}/email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subject, message }),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json();
}
async function patchJob(id, { start, duration_hours }) {
  const res = await fetch(`/api/jobs/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ start, duration_hours }),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json(); // { ok, start, end }
}
async function deleteJob(id) {
  const res = await fetch(`/api/jobs/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json(); // { ok: true }
}
async function prefillServiceReport(jobId, templateId) {
  const res = await fetch(`/api/service-reports/prefill?job_id=${jobId}&template_id=${templateId}`);
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json();
}

// --- overlap helpers (client-side UX) ---
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

export default function CalendarPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialPendingQuote = useMemo(() => {
    const quoteId = params.get("quoteId");
    if (!quoteId) return null;
    return {
      id: quoteId,
      number: params.get("quoteNumber") || "",
      customerName: params.get("name") || "",
    };
  }, [params]);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [pendingQuote, setPendingQuote] = useState(initialPendingQuote);

  // duration modal state (create flow)
  const [durationOpen, setDurationOpen] = useState(false);
  const [slotStart, setSlotStart] = useState(null);
  const [durationHours, setDurationHours] = useState(1);
  const [createConflict, setCreateConflict] = useState(false);

  // job details modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  // invoice preview modal
  const [invOpen, setInvOpen] = useState(false);
  const [invPdfUrl, setInvPdfUrl] = useState(undefined);
  const [invPdfName, setInvPdfName] = useState("invoice.pdf");
  const [invPdfErr, setInvPdfErr] = useState("");
  const [invNumPages, setInvNumPages] = useState(null);
  const [invEmailing, setInvEmailing] = useState(false);
  const [invEmailMsg, setInvEmailMsg] = useState("");
  const [currentInvoice, setCurrentInvoice] = useState(null);

  // reschedule modal
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedStart, setReschedStart] = useState("");   // datetime-local string
  const [reschedHours, setReschedHours] = useState(1);
  const [reschedConflict, setReschedConflict] = useState(false);
  const [actionMsg, setActionMsg] = useState("");

  // initial load
  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr("");
    fetchJobs()
      .then((evts) => active && setEvents(evts))
      .catch((e) => active && setErr(String(e.message || e)))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  // Make events fill the day columns
  const eventPropGetter = useCallback(() => {
    return {
      style: {
        backgroundColor: "#3b82f6",
        color: "#ffffff",
        border: "none",
        borderRadius: "6px",
        padding: "2px 4px",
        maxWidth: "none",
      },
    };
  }, []);

  // Select slot => duration picker
  const handleSelectSlot = useCallback(({ start, end }) => {
    if (!pendingQuote) {
      alert("To create a job, start from a quote (Book Job button).");
      return;
    }
    const diffMs = new Date(end).getTime() - new Date(start).getTime();
    const defaultHrs = Math.max(1, Math.round(diffMs / (60 * 60 * 1000)) || 1);
    const sMs = new Date(start).getTime();
    const eMs = sMs + defaultHrs * 3600000;
    const conflict = events.find(ev =>
      rangesOverlap(sMs, eMs, ev.start.getTime(), ev.end.getTime())
    );
    setCreateConflict(!!conflict);
    setSlotStart(new Date(start));
    setDurationHours(defaultHrs);
    setDurationOpen(true);
  }, [pendingQuote, events]);

  const recomputeCreateConflict = useCallback((hrs) => {
    if (!slotStart) return false;
    const s = slotStart.getTime();
    const e = s + Number(hrs) * 3600000;
    const conflict = events.find(ev =>
      rangesOverlap(s, e, ev.start.getTime(), ev.end.getTime())
    );
    setCreateConflict(!!conflict);
    return !!conflict;
  }, [slotStart, events]);

  const confirmCreateJob = useCallback(async () => {
    if (!pendingQuote || !slotStart || !durationHours) return;
    if (recomputeCreateConflict(durationHours)) {
      alert("That slot overlaps with another job. Please pick another time.");
      return;
    }
    try {
      setLoading(true);
      setErr("");
      setMsg("");

      const created = await createJob({
        quote_id: Number(pendingQuote.id),
        start: slotStart,
        duration_hours: Number(durationHours),
      });

      const newEvent = {
        id: created.id,
        title:
          created.title ||
          pendingQuote.customerName ||
          `Quote ${pendingQuote.number || created.quote_id}`,
        start: new Date(created.start),
        end: new Date(created.end),
        quoteId: created.quote_id,
      };
      setEvents((prev) => [...prev, newEvent]);

      setMsg("Job booked ✓");
      setPendingQuote(null);
      setDurationOpen(false);
      setSlotStart(null);
      navigate("/calendar", { replace: true });
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [pendingQuote, slotStart, durationHours, navigate, recomputeCreateConflict]);

  const handleSelectEvent = useCallback(async (evt) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await fetchJobDetail(evt.id);
      setDetail(d);
    } catch (e) {
      setDetail({ error: String(e.message || e) });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // --- Invoice preview helpers ---
  async function openInvoiceForJob() {
    if (!detail?.id) return;
    try {
      const paid = window.confirm("Invoice paid? Click OK for Yes, Cancel for No.");
      const r = await createInvoiceFromJob(detail.id, paid);

      const pdfRes = await fetch(`/api/invoices/${r.id}/pdf`);
      if (!pdfRes.ok) throw new Error(await pdfRes.text().catch(() => `HTTP ${pdfRes.status}`));
      const blob = await pdfRes.blob();
      if (invPdfUrl && String(invPdfUrl).startsWith("blob:")) URL.revokeObjectURL(invPdfUrl);
      const objUrl = URL.createObjectURL(blob);
      setCurrentInvoice(r);
      setInvPdfName(`invoice-${r.invoice_number}.pdf`);
      setInvPdfErr("");
      setInvNumPages(null);
      setInvPdfUrl(objUrl);
      setInvOpen(true);
    } catch (e) {
      alert(`Failed to create/open invoice: ${e.message || e}`);
    }
  }
  function toDatetimeLocalValue(d) {
    const pad = (n) => String(n).padStart(2, "0");
    const dt = new Date(d);
    const yyyy = dt.getFullYear();
    const mm = pad(dt.getMonth() + 1);
    const dd = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mi = pad(dt.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }
  function onInvoiceLoadSuccess({ numPages }) {
    setInvNumPages(numPages);
  }
  function downloadInvoicePdf() {
    if (!invPdfUrl) return;
    const a = document.createElement("a");
    a.href = invPdfUrl;
    a.download = invPdfName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  async function emailCurrentInvoice() {
    if (!currentInvoice?.id) return;
    setInvEmailing(true);
    setInvEmailMsg("");
    try {
      const subject = `Invoice ${currentInvoice.invoice_number}`;
      const message = `Hello,

Please find attached your invoice ${currentInvoice.invoice_number}.

Kind regards,`;
      await emailInvoice(currentInvoice.id, subject, message);
      setInvEmailMsg("Email sent ✓");
    } catch (e) {
      setInvEmailMsg(`Failed: ${e.message || e}`);
    } finally {
      setInvEmailing(false);
    }
  }

  // Reschedule conflict checking
  useEffect(() => {
    if (!reschedOpen || !detail) return;
    if (!reschedStart || !reschedHours) {
      setReschedConflict(false);
      return;
    }
    const s = new Date(reschedStart).getTime();
    const e = s + Number(reschedHours) * 3600000;
    const conflict = events.find(
      (ev) => ev.id !== detail.id && rangesOverlap(s, e, ev.start.getTime(), ev.end.getTime())
    );
    setReschedConflict(!!conflict);
  }, [reschedOpen, reschedStart, reschedHours, events, detail]);

  // === NEW: open service report handler ===
  const openServiceReport = useCallback(async () => {
    if (!detail?.id) return;

    try {
      // 1) Try server-side inference (job only).
      let pf = null;
      let inferred = false;
      try {
        const tryRes = await fetch(`/api/service-reports/prefill?job_id=${detail.id}`);
        if (tryRes.ok) {
          const data = await tryRes.json();
          if (data?.template?.id) {
            pf = data;
            inferred = true;
          }
        }
      } catch (_) {}

      // 2) If no template was inferred, ask the user which template to use
      if (!pf) {
        const tr = await fetch("/api/service-reports/templates");
        if (!tr.ok) throw new Error(await tr.text().catch(() => `HTTP ${tr.status}`));
        const tpls = await tr.json();
        if (!Array.isArray(tpls) || tpls.length === 0) {
          alert("No active templates. Add some in SR admin.");
          return;
        }

        let chosen = tpls[0].id;
        if (tpls.length > 1) {
          const names = tpls.map((t, i) => `${i + 1}) ${t.name}`).join("\n");
          const pick = window.prompt(`Select template:\n${names}\nEnter number:`, "1");
          const idx = Math.max(1, parseInt(pick || "1", 10)) - 1;
          chosen = tpls[idx]?.id || tpls[0].id;
        }

        // Call prefill with explicit template
        const res = await fetch(`/api/service-reports/prefill?job_id=${detail.id}&template_id=${chosen}`);
        if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
        pf = await res.json();
      }

      // 3) Store for the checklist page
      try {
        sessionStorage.setItem("SR_PREFILL", JSON.stringify(pf));
        sessionStorage.setItem("sr_job", String(detail.id));
        if (pf?.template?.id) sessionStorage.setItem("sr_template", String(pf.template.id));
      } catch {}

      // 4) Go to the engineer checklist
      window.location.href = "/service-reports/engineer";
    } catch (e) {
      alert(`Could not prefill service report: ${e.message || e}`);
    }
  }, [detail]);

  return (
    <div className="qp-container">
      {/* === MOBILE FULL-BLEED FIX: kill the left:50%/calc margins on phones only === */}
      <style>{`
        @media (max-width: 719.98px) {
          .qp-container {
            position: static !important;
            left: auto !important;
            right: auto !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .qp-wrap {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
          }
        }
      `}</style>

      {/* === Keep your existing RBC tweaks (unchanged) === */}
      <style>{`
        .rbc-time-view .rbc-events-container {
          margin-right: 0 !important;
          margin-left: 1px !important;
        }
        .rbc-time-view .rbc-day-slot .rbc-event {
          right: 0 !important;
          max-width: none !important;
        }
        .rbc-time-view .rbc-day-slot .rbc-event {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          line-height: 1.2;
          font-size: 13px;
          right: 0 !important;
          max-width: none !important;
        }
        .rbc-time-view .rbc-event-label,
        .rbc-time-view .rbc-event-content {
          white-space: normal;
          overflow: visible;
          text-overflow: clip;
          overflow-wrap: anywhere;
          text-align: center;
        }
        .rbc-time-view .rbc-event-label {
          font-weight: 600;
          margin-bottom: 2px;
        }
      `}</style>

      <div className="qp-wrap">
        {/* …everything below is unchanged… */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>Calendar</h1>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              View upcoming jobs and book new ones.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="qp-btn qp-btn--muted" onClick={() => navigate("/quotes")}>
              Back to Quotes
            </button>
            <button className="qp-btn qp-btn--muted" onClick={() => navigate("/invoices")}>
              Invoices
            </button>
          </div>
        </div>

        {pendingQuote && (
          <div style={{ marginBottom: 12, padding: 12, border: "1px solid #bfdbfe", background: "#dbeafe", borderRadius: 12, color: "#1e3a8a" }}>
            Booking for: <strong>{pendingQuote.customerName || `Quote ${pendingQuote.number || pendingQuote.id}`}</strong>.{" "}
            Select a time slot, then choose the duration.
          </div>
        )}

        {msg && (
          <div style={{ marginBottom: 12, padding: 12, border: "1px solid #bbf7d0", background: "#dcfce7", borderRadius: 12, color: "#166534" }}>
            {msg}
          </div>
        )}

        {actionMsg && (
          <div style={{ marginBottom: 12, padding: 12, border: "1px solid #bbf7d0", background: "#dcfce7", borderRadius: 12, color: "#166534" }}>
            {actionMsg}
          </div>
        )}

        {err && (
          <div style={{ marginBottom: 12, padding: 12, border: "1px solid #fecaca", background: "#fee2e2", borderRadius: 8, color: "#991b1b" }}>
            {err}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>Loading…</div>
        ) : (
          <div style={{ height: "calc(100vh - 280px)" }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "100%" }}
              selectable
              popup
              defaultView="week"
              step={30}
              timeslots={2}
              scrollToTime={new Date(1970, 1, 1, 8, 0)}
              eventPropGetter={eventPropGetter}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              views={["day", "week", "month", "agenda"]}
            />
          </div>
        )}

        {/* Duration Picker Modal */}
        {durationOpen && (
          <div className="qp-modal__backdrop" onClick={() => setDurationOpen(false)}>
            <div className="qp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="qp-modal__header">Select Duration</div>
              <div className="qp-modal__body" style={{ display: "grid", gap: 12 }}>
                <div>
                  <strong>Start:</strong> {slotStart ? slotStart.toLocaleString() : "-"}
                </div>
                <div>
                  <strong>Hours:</strong>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    {[1, 2, 3, 4, 6, 8].map((h) => (
                      <button
                        key={h}
                        className="qp-btn"
                        onClick={() => {
                          setDurationHours(h);
                          recomputeCreateConflict(h);
                        }}
                        style={{ background: durationHours === h ? "#111827" : undefined }}
                      >
                        {h}h
                      </button>
                    ))}
                    <input
                      className="qp-input"
                      type="number"
                      min={1}
                      step={1}
                      value={durationHours}
                      onChange={(e) => {
                        const v = Math.max(1, parseInt(e.target.value || "1", 10));
                        setDurationHours(v);
                        recomputeCreateConflict(v);
                      }}
                      style={{ width: 100 }}
                      placeholder="Custom"
                    />
                  </div>

                  {createConflict && (
                    <div style={{ marginTop: 10, padding: 10, border: "1px solid #fecaca", background: "#fee2e2", borderRadius: 8, color: "#991b1b" }}>
                      That slot overlaps with another job. Please pick another time.
                    </div>
                  )}
                </div>
              </div>
              <div className="qp-modal__footer">
                <button className="qp-btn qp-btn--muted" onClick={() => setDurationOpen(false)}>Cancel</button>
                <button className="qp-btn" onClick={confirmCreateJob} disabled={createConflict}>Create Job</button>
              </div>
            </div>
          </div>
        )}

        {/* Job Detail Modal */}
        {detailOpen && (
          <div className="qp-modal__backdrop" onClick={() => setDetailOpen(false)}>
            <div className="qp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="qp-modal__header">Job Details</div>
              <div className="qp-modal__body">
                {detailLoading ? (
                  <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>Loading…</div>
                ) : detail?.error ? (
                  <div style={{ padding: 12, border: "1px solid #fecaca", background: "#fee2e2", borderRadius: 8, color: "#991b1b" }}>
                    {detail.error}
                  </div>
                ) : detail ? (
                  <div style={{ display: "grid", gap: 12 }}>
                    <div><strong>Quote:</strong> {detail.quote_number || detail.quote_id}</div>
                    <div><strong>When:</strong> {new Date(detail.start).toLocaleString()} – {new Date(detail.end).toLocaleString()}</div>
                    <div>
                      <strong>Customer:</strong> {detail.customer?.name || "-"}
                      <div style={{ color: "#374151", marginTop: 4 }}>{detail.customer?.address || "-"}</div>
                      <div style={{ color: "#374151", marginTop: 2 }}>
                        {detail.customer?.tel ? `Tel: ${detail.customer.tel}` : ""} {detail.customer?.email ? `  Email: ${detail.customer.email}` : ""}
                      </div>
                    </div>
                    <div>
                      <strong>Items:</strong>
                      <ul style={{ marginTop: 6, marginLeft: 16 }}>
                        {(detail.items || []).map((ln, i) => <li key={i}>{ln}</li>)}
                      </ul>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="qp-modal__footer">
                <button className="qp-btn" onClick={openInvoiceForJob}>Invoice</button>

                <button
                  className="qp-btn"
                  onClick={() => {
                    if (!detail) return;
                    const mins = Math.max(60, Math.round((new Date(detail.end) - new Date(detail.start)) / 60000));
                    setReschedHours(Math.max(1, Math.round(mins / 60)));
                    setReschedStart(toDatetimeLocalValue(detail.start));
                    setReschedOpen(true);
                  }}
                >
                  Reschedule
                </button>

                {/* === NEW: Service report === */}
                <button className="qp-btn" onClick={openServiceReport}>
                  Service report
                </button>

                <button
                  className="qp-btn qp-btn--muted"
                  onClick={async () => {
                    if (!detail?.id) return;
                    if (!window.confirm("Cancel this job?")) return;
                    try {
                      await deleteJob(detail.id);
                      setEvents((prev) => prev.filter((e) => e.id !== detail.id));
                      setDetailOpen(false);
                      setActionMsg("Job cancelled ✓");
                      setTimeout(() => setActionMsg(""), 3000);
                    } catch (e) {
                      alert(`Failed to cancel: ${e.message || e}`);
                    }
                  }}
                >
                  Cancel Job
                </button>

                <button className="qp-btn" onClick={() => setDetailOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Reschedule Modal */}
        {reschedOpen && (
          <div className="qp-modal__backdrop" onClick={() => setReschedOpen(false)}>
            <div className="qp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="qp-modal__header">Reschedule Job</div>
              <div className="qp-modal__body" style={{ display: "grid", gap: 12 }}>
                <div>
                  <div className="qp-label">Start</div>
                  <input
                    className="qp-input"
                    type="datetime-local"
                    value={reschedStart}
                    onChange={(e) => setReschedStart(e.target.value)}
                  />
                </div>
                <div>
                  <div className="qp-label">Duration (hours)</div>
                  <input
                    className="qp-input"
                    type="number"
                    min={1}
                    step={1}
                    value={reschedHours}
                    onChange={(e) => setReschedHours(Math.max(1, parseInt(e.target.value || "1", 10)))}
                    style={{ width: 120 }}
                  />
                </div>

                {reschedConflict && (
                  <div style={{ marginTop: 4, padding: 10, border: "1px solid #fecaca", background: "#fee2e2", borderRadius: 8, color: "#991b1b" }}>
                    That slot overlaps with another job. Please pick another time.
                  </div>
                )}
              </div>
              <div className="qp-modal__footer">
                <button className="qp-btn qp-btn--muted" onClick={() => setReschedOpen(false)}>Cancel</button>
                <button
                  className="qp-btn"
                  disabled={reschedConflict}
                  onClick={async () => {
                    if (!detail?.id) return;
                    try {
                      const r = await patchJob(detail.id, {
                        start: reschedStart,
                        duration_hours: reschedHours,
                      });
                      setEvents((prev) =>
                        prev.map((ev) =>
                          ev.id === detail.id ? { ...ev, start: new Date(r.start), end: new Date(r.end) } : ev
                        )
                      );
                      setDetail((d) => (d ? { ...d, start: r.start, end: r.end } : d));
                      setReschedOpen(false);
                      setActionMsg("Job rescheduled ✓");
                      setTimeout(() => setActionMsg(""), 3000);
                    } catch (e) {
                      alert(`Failed to reschedule: ${e.message || e}`);
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice PDF Modal */}
        {invOpen && (
          <div className="qp-modal__backdrop" onClick={() => setInvOpen(false)}>
            <div className="qp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="qp-modal__header">Invoice Preview</div>
              <div className="qp-modal__body">
                {invPdfErr ? (
                  <div style={{ padding: 24, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8 }}>
                    Failed to load PDF: {invPdfErr}
                  </div>
                ) : !invPdfUrl ? (
                  <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>Loading PDF…</div>
                ) : (
                  <Document
                    file={invPdfUrl}
                    onLoadSuccess={({ numPages }) => onInvoiceLoadSuccess({ numPages })}
                    onLoadError={(e) => setInvPdfErr(String(e?.message || e))}
                    onSourceError={(e) => setInvPdfErr(String(e?.message || e))}
                  >
                    {Array.from(new Array(invNumPages || 0), (_, idx) => (
                      <Page key={`invp_${idx + 1}`} pageNumber={idx + 1} width={800} />
                    ))}
                  </Document>
                )}
              </div>
              <div className="qp-modal__footer">
                {invPdfUrl && (
                  <>
                    <button className="qp-btn" onClick={downloadInvoicePdf}>Download</button>
                    <button className="qp-btn" onClick={emailCurrentInvoice} disabled={invEmailing}>
                      {invEmailing ? "Sending…" : "Email to Customer"}
                    </button>
                    {invEmailMsg && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: invEmailMsg.startsWith("Email sent") ? "#15803d" : "#b91c1c" }}>
                        {invEmailMsg}
                      </span>
                    )}
                  </>
                )}
                <button className="qp-btn" onClick={() => setInvOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
